import React, { useEffect, useState, useRef } from "react";
import Confetti from "react-confetti";
import PropertyCard from "../../components/properties/PropertyCard";
import MapView from "./MapView";
import "../../styles/styles.css";
import {
  Avatar,
  Box,
  Checkbox,
  Chip,
  Fab,
  Pagination,
  Tooltip,
  Typography,
  Menu,
  MenuItem,
  ListItemText,
  Divider,
  Button,
  IconButton,
} from "@mui/material";
import { useProperty } from "../../context/PropertyContext";
import {
  Map as MapIcon,
  List as ListIcon,
  Sort as SortIcon,
  PictureAsPdf,
  KeyboardArrowUp,
  KeyboardArrowDown,
} from "@mui/icons-material";
import Grid from "@mui/material/Grid2";
import { PulseLoader } from "react-spinners";
import NotInterestedIcon from "@mui/icons-material/NotInterested";
import PriceConversionModal from "../../components/modals/PriceConversionModal";
import { AnimatePresence, motion } from "framer-motion";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import {
  faChevronRight,
  faChevronLeft,
  faCoins,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useCurrency } from "../../context/CurrencyContext";
import ChatBubble from "../../components/ChatBubble";
import { PDFDownloadLink } from "@react-pdf/renderer";
import PropertyPDF from "../../components/pdf/PDFProperty";
import { useAuth } from "../../context/AuthContext";
import {
  provinces,
  zoneMapping,
  zoneIcons,
  ZoneKey,
} from "../../types/locations";
import { useLocation } from "react-router-dom/dist";

const images = [
  "/assets/images/1.png",
  "/assets/images/logo-alien5-v3.png",
  "/assets/images/logo-alien6-v3.png",
];

const HomePage: React.FC = () => {
  const [viewMode, setViewMode] = useState<"list" | "map">("list");
  const [isModalOpen, setModalOpen] = useState(false);
  const [selectedPrice, setSelectedPrice] = useState<number>(0);
  const [selectedCurrency2, setSelectedCurrency2] = useState<string>("");
  const {
    filteredProperties,
    filterPropertiesByProvinceAndCanton,
    filterPropertiesByLocation,
    loading,
    error,
    totalProperties,
    fetchProperties, 
  } = useProperty();
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 25;
  const { selectedCurrency, sellRate, buyRate } = useCurrency();
  const { name, email } = useAuth();
  const [currencyFilter, setCurrencyFilter] = useState<string>("all");
  const [filterType, setFilterType] = useState<string>("none");
  const [currencyAnchorEl, setCurrencyAnchorEl] = useState<null | HTMLElement>(
    null
  );
  const [sortAnchorEl, setSortAnchorEl] = useState<null | HTMLElement>(null);
  const openCurrencyMenu = Boolean(currencyAnchorEl);
  const openSortMenu = Boolean(sortAnchorEl);
  const [imageIndex, setImageIndex] = useState(0);
  const location = useLocation();

  useEffect(() => {
    const interval = setInterval(() => {
      setImageIndex((prev) => (prev + 1) % images.length);
    }, 1700);
    return () => clearInterval(interval);
  });


  const handleCurrencyClick = (event: React.MouseEvent<HTMLElement>) => {
    setCurrencyAnchorEl(event.currentTarget);
  };

  const handleSortClick = (event: React.MouseEvent<HTMLElement>) => {
    setSortAnchorEl(event.currentTarget);
  };

  const handleCurrencyClose = () => {
    setCurrencyAnchorEl(null);
  };

  const handleSortClose = () => {
    setSortAnchorEl(null);
  };

  const handleCurrencySelect = (currency: string) => {
    setCurrencyFilter(currency);
    handleCurrencyClose();
  };

  const handleFilterTypeSelect = (type: string) => {
    setFilterType(type);
    handleSortClose();
  };


  let filteredByOptions = [...filteredProperties];

  if (currencyFilter !== "all") {
    filteredByOptions = filteredByOptions.filter(
      (property) => property.currency === currencyFilter
    );
  }
  switch (filterType) {
    case "date":
      filteredByOptions.sort(
        (a, b) =>
          new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime()
      );
      break;
    case "sale":
      filteredByOptions = filteredByOptions.filter(
        (property) => property.modeDescription === "Venta"
      );
      break;
    case "rent":
      filteredByOptions = filteredByOptions.filter(
        (property) => property.modeDescription === "Alquiler"
      );
      break;
    case "price_high":
      filteredByOptions.sort((a, b) => b.price - a.price);
      break;
    case "price_low":
      filteredByOptions.sort((a, b) => a.price - b.price);
      break;
    default:
      // No filter
      break;
  }


    // Número máximo de páginas cargadas (cada página tiene itemsPerPage items)
  const loadedPages = Math.max(
    1,
    Math.floor(filteredProperties.length / itemsPerPage)
  );

  // Limitar máximo de páginas a mostrar
  const maxAllowedPage = loadedPages + 5;

  // Página segura (no excede maxAllowedPage)
  const safeCurrentPage =
    currentPage > maxAllowedPage ? maxAllowedPage : currentPage;

  // Si currentPage es mayor a las páginas cargadas, mostrar última página cargada
  const pageToShow = safeCurrentPage <= loadedPages ? safeCurrentPage : loadedPages;

  // Obtener los items actuales para mostrar
  const currentProperties = filteredByOptions.slice(
    (pageToShow - 1) * itemsPerPage,
    pageToShow * itemsPerPage
  );

  const handlePageChange = async (
    event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    // Limitar la página que el usuario puede seleccionar
    const pageToLoad = page > maxAllowedPage ? maxAllowedPage : page;

    // Verificar si es necesario cargar más páginas
    if (
      filteredProperties.length < pageToLoad * itemsPerPage &&
      filteredProperties.length < totalProperties
    ) {
      for (let p = loadedPages + 1; p <= pageToLoad; p++) {
        await fetchProperties(p, itemsPerPage, false);
      }
    }

    setCurrentPage(pageToLoad);
  };

  const [showCelebration, setShowCelebration] = useState(false);
  const [showTitle, setShowTitle] = useState(false);
  const [selectedProvinces, setSelectedProvinces] = useState<string[]>([]);
  const [selectedCantons, setSelectedCantons] = useState<string[]>([]);
  const [expandedCantons, setExpandedCantons] = useState<string[]>([]);
  const [selectedDistricts, setSelectedDistricts] = useState<string[]>([]);
  const [selectedZones, setSelectedZones] = useState<ZoneKey[]>([]);
  const [filterView, setFilterView] = useState<"provinces" | "zones">(
    "provinces"
  );
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const [isFilterPanelExpanded, setIsFilterPanelExpanded] = useState(true);

  const handleProvinceSelect = (province: string) => {
    setSelectedProvinces((prevSelectedProvinces) => {
      const newSelectedProvinces = prevSelectedProvinces.includes(province)
        ? prevSelectedProvinces.filter((item) => item !== province)
        : [...prevSelectedProvinces, province];

      let updatedCantons = [...selectedCantons];
      let updatedDistricts = [...selectedDistricts];
      if (!newSelectedProvinces.includes(province)) {
        updatedCantons = updatedCantons.filter(
          (canton) => !canton.startsWith(province)
        );
        updatedDistricts = updatedDistricts.filter(
          (district) => !district.startsWith(province)
        );
      }

      setSelectedCantons(updatedCantons);
      setSelectedDistricts(updatedDistricts);

      filterPropertiesByLocation(
        newSelectedProvinces,
        updatedCantons,
        updatedDistricts
      );

      return newSelectedProvinces;
    });
  };

  const handleCantonSelect = (provinceName: string, cantonName: string) => {
    setSelectedCantons((prevSelectedCantons) => {
      const cantonKey = `${provinceName}-${cantonName}`;
      const newSelectedCantons = prevSelectedCantons.includes(cantonKey)
        ? prevSelectedCantons.filter((item) => item !== cantonKey)
        : [...prevSelectedCantons, cantonKey];

      if (!prevSelectedCantons.includes(cantonKey)) {
        setExpandedCantons((prev) => [...prev, cantonKey]);
      }

      let updatedDistricts = [...selectedDistricts];
      if (!newSelectedCantons.includes(cantonKey)) {
        updatedDistricts = updatedDistricts.filter(
          (district) => !district.startsWith(cantonKey)
        );
        setSelectedDistricts(updatedDistricts);
      }

      filterPropertiesByLocation(
        selectedProvinces,
        newSelectedCantons,
        updatedDistricts
      );

      return newSelectedCantons;
    });
  };

  const handleDistrictSelect = (
    provinceName: string,
    cantonName: string,
    districtName: string
  ) => {
    setSelectedDistricts((prevSelectedDistricts) => {
      const districtKey = `${provinceName}-${cantonName}-${districtName}`;
      const newSelectedDistricts = prevSelectedDistricts.includes(districtKey)
        ? prevSelectedDistricts.filter((item) => item !== districtKey)
        : [...prevSelectedDistricts, districtKey];

      filterPropertiesByLocation(
        selectedProvinces,
        selectedCantons,
        newSelectedDistricts
      );

      return newSelectedDistricts;
    });
  };

  const toggleCantonExpansion = (cantonKey: string) => {
    setExpandedCantons((prev) =>
      prev.includes(cantonKey)
        ? prev.filter((key) => key !== cantonKey)
        : [...prev, cantonKey]
    );
  };

  const handleZoneSelect = (zone: ZoneKey) => {
    setSelectedZones((prevSelectedZones) => {
      const newSelectedZones = prevSelectedZones.includes(zone)
        ? prevSelectedZones.filter((item) => item !== zone)
        : [...prevSelectedZones, zone];

      let updatedCantons = [...selectedCantons];
      const updatedProvinces = [...selectedProvinces];

      if (newSelectedZones.includes(zone)) {
        const cantonsInZone = zoneMapping[zone] || [];
        cantonsInZone.forEach((cantonKey: string) => {
          if (!updatedCantons.includes(cantonKey)) {
            updatedCantons.push(cantonKey);

            const province = cantonKey.split("-")[0];
            if (!updatedProvinces.includes(province)) {
              updatedProvinces.push(province);
            }
          }
        });
      } else {
        updatedCantons = updatedCantons.filter((cantonKey) => {
          const belongsToOtherSelectedZone = newSelectedZones.some(
            (selectedZone) =>
              (zoneMapping[selectedZone as ZoneKey] || []).includes(cantonKey)
          );
          return belongsToOtherSelectedZone;
        });

        const provincesInRemainingCantons = new Set<string>();
        updatedCantons.forEach((cantonKey) => {
          const province = cantonKey.split("-")[0];
          provincesInRemainingCantons.add(province);
        });

        updatedProvinces.length = 0;
        provincesInRemainingCantons.forEach((province) => {
          updatedProvinces.push(province);
        });
      }

      setSelectedProvinces(updatedProvinces);
      setSelectedCantons(updatedCantons);

      filterPropertiesByProvinceAndCanton(updatedProvinces, updatedCantons);

      return newSelectedZones;
    });
  };

  const isCompanyPath = location.pathname.startsWith("/company/");

  useEffect(() => {
    // 🚫 Si estamos en una compañía, no mostrar animaciones
    if (isCompanyPath) {
      setShowCelebration(false);
      setShowTitle(false);
      return;
    }

    if (!loading) {
      setShowCelebration(true);
      setShowTitle(true);

      const hideTitleTimer = setTimeout(() => setShowTitle(false), 5000);
      const stopConfettiTimer = setTimeout(() => setShowCelebration(false), 7000);

      return () => {
        clearTimeout(hideTitleTimer);
        clearTimeout(stopConfettiTimer);
      };
    }
  }, [loading, isCompanyPath]);

  const handleOpenModal = (price: number, currency: string) => {
    setSelectedPrice(price);
    setSelectedCurrency2(currency);
    setModalOpen(true);
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const toggleFilterView = () => {
    setFilterView(filterView === "provinces" ? "zones" : "provinces");
  };

  const isGAMZone = (zone: ZoneKey): boolean => {
    return zone.endsWith("(GAM)") && !zone.includes("Fuera del GAM");
  };

  const isOutsideGAMZone = (zone: ZoneKey): boolean => {
    return zone.includes("Fuera del GAM");
  };

  const toggleFilterPanel = () => {
    setIsFilterPanelExpanded(!isFilterPanelExpanded);
  };
  return (
    <Box>
      <Box
        sx={{
          position: "fixed",
           width: "100%",
          zIndex: 1100,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: 1,
          pt: 2,
          backgroundColor: "#fff",
        }}
      >
        <Tooltip title="Filtrar por moneda">
          <Box
            onClick={handleCurrencyClick}
            sx={{
              cursor: "pointer",
              paddingLeft: 3,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 24,
              height: 24,
            }}
          >
            <FontAwesomeIcon icon={faCoins} color="#000" size="xs" />
          </Box>
        </Tooltip>
        <Menu
          anchorEl={currencyAnchorEl}
          open={openCurrencyMenu}
          onClose={handleCurrencyClose}
        >
          <MenuItem onClick={() => handleCurrencySelect("all")}>
            <ListItemText>Todas</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => handleCurrencySelect("USD")}>
            <ListItemText>$ Dolares (USD)</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => handleCurrencySelect("CRC")}>
            <ListItemText>₡ Colones (CRC)</ListItemText>
          </MenuItem>
        </Menu>

        <Tooltip title="Ordenar y filtrar">
          <Box
            onClick={handleSortClick}
            sx={{
              cursor: "pointer",
              paddingRight: 4,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 24,
              height: 24,
            }}
          >
            <SortIcon fontSize="small" />
          </Box>
        </Tooltip>
        <Menu
          anchorEl={sortAnchorEl}
          open={openSortMenu}
          onClose={handleSortClose}
        >
          <MenuItem onClick={() => handleFilterTypeSelect("none")}>
            <ListItemText>Sin filtro</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => handleFilterTypeSelect("date")}>
            <ListItemText>Fecha</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => handleFilterTypeSelect("sale")}>
            <ListItemText>Solo Venta</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => handleFilterTypeSelect("rent")}>
            <ListItemText>Solo Alquiler</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => handleFilterTypeSelect("price_high")}>
            <ListItemText>Precio: Mayor a menor</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => handleFilterTypeSelect("price_low")}>
            <ListItemText>Precio: Menor a mayor</ListItemText>
          </MenuItem>
        </Menu>
      </Box>
      <Box
        style={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            width: sidebarOpen ? 180 : 0,
            backgroundColor: sidebarOpen
              ? "rgba(244, 244, 244, 0.98)"
              : "transparent",
            display: "flex",
            flexDirection: "column",
            position: "fixed",
            padding: sidebarOpen ? 2 : 0,
            zIndex: 2000,
            height: "100vh",
            transition: "width 0.3s ease, background-color 0.3s ease",
            alignItems: "center",
            justifyContent: "flex-start",
            boxShadow: sidebarOpen ? "2px 0 10px rgba(0, 0, 0, 0.1)" : "none",
            paddingBottom: "60px",
          }}
        >
          {/* Botón para abrir o cerrar el sidebar */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              right: -15,
              zIndex: 1001,
              cursor: "pointer",
            }}
            onClick={toggleSidebar}
          >
            {sidebarOpen ? (
              <FontAwesomeIcon icon={faChevronLeft} size="1x" />
            ) : (
              <FontAwesomeIcon icon={faChevronRight} size="1x" />
            )}
          </Box>

          {/* Contenido del Sidebar */}
          <Box
            ref={sidebarRef}
            sx={{
              display: sidebarOpen ? "flex" : "none",
              flexDirection: "column",
              alignItems: "center",
              width: "100%",
              height: "100%",
              flex: 1,
              overflowY: "auto",
              paddingBottom: "100px",
            }}
          >
            <Typography variant="body1" sx={{ textAlign: "center", mb: 4 }}>
              Filtra por ubicación
            </Typography>

            {/* Toggle between provinces and zones */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mb: 2,
                width: "100%",
              }}
            >
              <Chip
                label="Provincias"
                onClick={toggleFilterView}
                color={filterView === "provinces" ? "primary" : "default"}
                sx={{
                  mr: 0.5,
                  fontSize: "10px",
                  flex: 1,
                  backgroundColor:
                    filterView === "provinces"
                      ? "rgba(0, 123, 255, 0.65)"
                      : "rgba(0, 0, 0, 0.1)",
                }}
              />
              <Chip
                label="Zonas"
                onClick={toggleFilterView}
                color={filterView === "zones" ? "primary" : "default"}
                sx={{
                  ml: 0.5,
                  fontSize: "10px",
                  flex: 1,
                  backgroundColor:
                    filterView === "zones"
                      ? "rgba(0, 123, 255, 0.65)"
                      : "rgba(0, 0, 0, 0.1)",
                }}
              />
            </Box>

            <Box
              sx={{
                width: "100%",
                paddingRight: { xs: 1, sm: 1 },
              }}
            >
              {filterView === "provinces" ? (
                // Provinces view
                <Grid
                  container
                  direction="column"
                  spacing={2}
                  sx={{
                    flexGrow: 1,
                    justifyContent: "flex-start",
                  }}
                >
                  {provinces.map((province) => (
                    <Grid
                      key={province.name}
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        flexDirection: "column",
                        alignItems: "center",
                      }}
                    >
                      <Chip
                        avatar={
                          <Avatar
                            src={province.flag}
                            alt={`Bandera de ${province.name}`}
                          />
                        }
                        label={province.name}
                        onClick={() => handleProvinceSelect(province.name)}
                        color={
                          selectedProvinces.includes(province.name)
                            ? "primary"
                            : "default"
                        }
                        sx={{
                          width: "90%",
                          justifyContent: "flex-start",
                          fontSize: { xs: "14px", sm: "12px" },
                          flexGrow: 1,
                          backgroundColor: selectedProvinces.includes(
                            province.name
                          )
                            ? "rgba(0, 123, 255, 0.65)"
                            : "rgba(0, 0, 0, 0.1)",
                          borderRadius: "20px",
                          transition: "background-color 0.3s ease",
                          padding: { xs: "10px 8px", sm: "8px" },
                          height: { xs: "auto", sm: "32px" },
                          "& .MuiChip-label": {
                            padding: { xs: "0 8px", sm: "0 6px" },
                          },
                          "&:hover": {
                            backgroundColor: selectedProvinces.includes(
                              province.name
                            )
                              ? "rgba(0, 123, 255, 0.4)"
                              : "rgba(0, 0, 0, 0.2)",
                          },
                        }}
                      />
                      <Box
                        sx={{
                          display: selectedProvinces.includes(province.name)
                            ? "block"
                            : "none",
                          width: "100%",
                          marginTop: 1,
                          marginLeft: 1,
                        }}
                      >
                        {province.cantons.map((canton) => (
                          <Box key={canton.name}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                marginBottom: 1,
                                cursor: "pointer",
                              }}
                            >
                              <Checkbox
                                checked={selectedCantons.includes(
                                  `${province.name}-${canton.name}`
                                )}
                                onChange={() =>
                                  handleCantonSelect(province.name, canton.name)
                                }
                                sx={{
                                  padding: 0,
                                  "& .MuiSvgIcon-root": {
                                    fontSize: { xs: 20, sm: 16 },
                                  },
                                }}
                              />
                              <Typography
                                sx={{
                                  fontSize: { xs: "14px", sm: "12px" },
                                  marginLeft: "8px",
                                  fontWeight: expandedCantons.includes(
                                    `${province.name}-${canton.name}`
                                  )
                                    ? "bold"
                                    : "normal",
                                  cursor: "pointer",
                                }}
                                onClick={() =>
                                  toggleCantonExpansion(
                                    `${province.name}-${canton.name}`
                                  )
                                }
                              >
                                {canton.name}
                              </Typography>
                              <Box
                                sx={{
                                  ml: 1,
                                  cursor: "pointer",
                                  fontSize: "10px",
                                  color: "text.secondary",
                                }}
                                onClick={() =>
                                  toggleCantonExpansion(
                                    `${province.name}-${canton.name}`
                                  )
                                }
                              >
                                {expandedCantons.includes(
                                  `${province.name}-${canton.name}`
                                )
                                  ? "▼"
                                  : "►"}
                              </Box>
                            </Box>

                            {/* Distritos */}
                            {expandedCantons.includes(
                              `${province.name}-${canton.name}`
                            ) && (
                              <Box sx={{ ml: 3, mb: 1 }}>
                                {canton.districts.map((district) => (
                                  <Box
                                    key={district.name}
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      mb: 0.5,
                                    }}
                                  >
                                    <Checkbox
                                      checked={selectedDistricts.includes(
                                        `${province.name}-${canton.name}-${district.name}`
                                      )}
                                      onChange={() =>
                                        handleDistrictSelect(
                                          province.name,
                                          canton.name,
                                          district.name
                                        )
                                      }
                                      sx={{
                                        padding: 0,
                                        "& .MuiSvgIcon-root": {
                                          fontSize: { xs: 18, sm: 14 },
                                        },
                                      }}
                                    />
                                    <Typography
                                      sx={{
                                        fontSize: { xs: "13px", sm: "11px" },
                                        ml: 1,
                                      }}
                                    >
                                      {district.name}
                                    </Typography>
                                  </Box>
                                ))}
                              </Box>
                            )}
                          </Box>
                        ))}
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                // Zones view
                <Grid
                  container
                  direction="column"
                  spacing={1}
                  sx={{
                    flexGrow: 1,
                    justifyContent: "flex-start",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: "bold",
                      mb: 1,
                      pl: 1,
                      position: "sticky",
                      top: 0,
                      backgroundColor: "rgba(244, 244, 244, 0.98)",
                      zIndex: 10,
                      padding: "5px 10px",
                      width: "100%",
                      borderRadius: "4px",
                    }}
                  >
                    Gran Área Metropolitana
                  </Typography>
                  {(Object.keys(zoneMapping) as ZoneKey[])
                    .filter(isGAMZone)
                    .map((zone) => (
                      <Grid
                        key={zone}
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          flexDirection: "column",
                          alignItems: "center",
                          mb: 1,
                        }}
                      >
                        <Chip
                          avatar={
                            <Box
                              sx={{
                                width: { xs: 28, sm: 24 },
                                height: { xs: 28, sm: 24 },
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              {zoneIcons[zone]}
                            </Box>
                          }
                          label={zone}
                          onClick={() => handleZoneSelect(zone)}
                          color={
                            selectedZones.includes(zone) ? "primary" : "default"
                          }
                          sx={{
                            width: "90%",
                            justifyContent: "flex-start",
                            fontSize: { xs: "13px", sm: "11px" },
                            flexGrow: 1,
                            backgroundColor: selectedZones.includes(zone)
                              ? "rgba(0, 123, 255, 0.65)"
                              : "rgba(0, 0, 0, 0.1)",
                            borderRadius: "20px",
                            transition: "background-color 0.3s ease",
                            padding: { xs: "10px 8px", sm: "8px" },
                            height: { xs: "auto", sm: "32px" },
                            "& .MuiChip-label": {
                              padding: { xs: "0 8px", sm: "0 6px" },
                            },
                            "&:hover": {
                              backgroundColor: selectedZones.includes(zone)
                                ? "rgba(0, 123, 255, 0.4)"
                                : "rgba(0, 0, 0, 0.2)",
                            },
                          }}
                        />
                      </Grid>
                    ))}

                  <Divider sx={{ my: 1 }} />

                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: "bold",
                      mb: 1,
                      pl: 1,
                      position: "sticky",
                      top: 0,
                      backgroundColor: "rgba(244, 244, 244, 0.98)",
                      zIndex: 10,
                      padding: "5px 10px",
                      width: "100%",
                      borderRadius: "4px",
                    }}
                  >
                    Fuera del GAM
                  </Typography>
                  {(Object.keys(zoneMapping) as ZoneKey[])
                    .filter(isOutsideGAMZone)
                    .map((zone) => (
                      <Grid
                        key={zone}
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          flexDirection: "column",
                          alignItems: "center",
                          mb: 1,
                        }}
                      >
                        <Chip
                          avatar={
                            <Box
                              sx={{
                                width: { xs: 28, sm: 24 },
                                height: { xs: 28, sm: 24 },
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              {zoneIcons[zone]}
                            </Box>
                          }
                          label={zone.replace(" (Fuera del GAM)", "")}
                          onClick={() => handleZoneSelect(zone)}
                          color={
                            selectedZones.includes(zone) ? "primary" : "default"
                          }
                          sx={{
                            width: "90%",
                            justifyContent: "flex-start",
                            fontSize: { xs: "13px", sm: "11px" },
                            flexGrow: 1,
                            backgroundColor: selectedZones.includes(zone)
                              ? "rgba(0, 123, 255, 0.65)"
                              : "rgba(0, 0, 0, 0.1)",
                            borderRadius: "20px",
                            transition: "background-color 0.3s ease",
                            padding: { xs: "10px 8px", sm: "8px" },
                            height: { xs: "auto", sm: "32px" },
                            "& .MuiChip-label": {
                              padding: { xs: "0 8px", sm: "0 6px" },
                            },
                            "&:hover": {
                              backgroundColor: selectedZones.includes(zone)
                                ? "rgba(0, 123, 255, 0.4)"
                                : "rgba(0, 0, 0, 0.2)",
                            },
                          }}
                        />
                      </Grid>
                    ))}
                </Grid>
              )}
            </Box>
            {/* Panel de filtros activos flotante y colapsable */}
            {sidebarOpen &&
              (selectedProvinces.length > 0 || selectedZones.length > 0) && (
                <Box
                  sx={{
                    position: "fixed",
                    left: 0,
                    bottom: 0,
                    width: 180,
                    backgroundColor: "rgba(255, 255, 255, 0.95)",
                    borderTop: "1px solid rgba(0, 0, 0, 0.1)",
                    boxShadow: "0 -2px 10px rgba(0,0,0,0.1)",
                    zIndex: 2001,
                    transition: "all 0.3s ease",
                  }}
                >
                  {/* Cabecera del panel con botón para colapsar/expandir */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "8px 12px",
                      backgroundColor: "rgba(0, 123, 255, 0.1)",
                      cursor: "pointer",
                    }}
                    onClick={toggleFilterPanel}
                  >
                    <Typography variant="caption" sx={{ fontWeight: "bold" }}>
                      Filtros activos (
                      {selectedProvinces.length + selectedZones.length})
                    </Typography>
                    <IconButton size="small" sx={{ padding: 0 }}>
                      {isFilterPanelExpanded ? (
                        <KeyboardArrowDown fontSize="small" />
                      ) : (
                        <KeyboardArrowUp fontSize="small" />
                      )}
                    </IconButton>
                  </Box>

                  {/* Contenido del panel (colapsable) */}
                  {isFilterPanelExpanded && (
                    <Box sx={{ padding: "8px 12px" }}>
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 0.5,
                          mb: 1,
                        }}
                      >
                        {selectedZones.length > 0 && (
                          <Chip
                            label={`${selectedZones.length} zona${
                              selectedZones.length !== 1 ? "s" : ""
                            }`}
                            size="small"
                            sx={{
                              height: "20px",
                              fontSize: "10px",
                              "& .MuiChip-label": { px: 1 },
                            }}
                          />
                        )}
                        {selectedProvinces.length > 0 && (
                          <Chip
                            label={`${selectedProvinces.length} provincia${
                              selectedProvinces.length !== 1 ? "s" : ""
                            }`}
                            size="small"
                            sx={{
                              height: "20px",
                              fontSize: "10px",
                              "& .MuiChip-label": { px: 1 },
                            }}
                          />
                        )}
                        {selectedCantons.length > 0 && (
                          <Chip
                            label={`${selectedCantons.length} cantón${
                              selectedCantons.length !== 1 ? "es" : ""
                            }`}
                            size="small"
                            sx={{
                              height: "20px",
                              fontSize: "10px",
                              "& .MuiChip-label": { px: 1 },
                            }}
                          />
                        )}
                        {selectedDistricts.length > 0 && (
                          <Chip
                            label={`${selectedDistricts.length} distrito${
                              selectedDistricts.length !== 1 ? "s" : ""
                            }`}
                            size="small"
                            sx={{
                              height: "20px",
                              fontSize: "10px",
                              "& .MuiChip-label": { px: 1 },
                            }}
                          />
                        )}
                      </Box>

                      <Typography
                        variant="caption"
                        sx={{ display: "block", mb: 0.5, fontWeight: "bold" }}
                      >
                        {filteredByOptions.length} propiedades encontradas
                      </Typography>

                      {filteredByOptions.length > 0 && (
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          startIcon={<PictureAsPdf />}
                          sx={{
                            borderRadius: "20px",
                            fontSize: { xs: "12px", sm: "10px" },
                            mt: 1,
                            width: "100%",
                            textTransform: "none",
                            padding: { xs: "6px 12px", sm: "4px 10px" },
                          }}
                          component={PDFDownloadLink}
                          document={
                            <PropertyPDF
                              properties={filteredByOptions}
                              selectedZones={selectedZones}
                              selectedProvinces={selectedProvinces}
                              selectedCantons={selectedCantons}
                              selectedDistricts={selectedDistricts}
                              currencyFilter={currencyFilter}
                              filterType={filterType}
                              generatedBy={name || "Usuario"}
                              userPhotoUrl={
                                "/assets/images/icono-alien-realty-2--.png"
                              }
                              userEmail={email || ""}
                            />
                          }
                          fileName="propiedades-filtradas.pdf"
                        >
                          Generar PDF
                        </Button>
                      )}
                    </Box>
                  )}
                </Box>
              )}
          </Box>
          <Box sx={{ width: "100%", mt: 1 }}>
            <img
              src="/assets/images/logo-alien-realty-2.png"
              alt="test scroll"
              style={{ width: "100%", height: "auto" }}
            />
          </Box>
        </Box>
      </Box>
    {(showTitle || showCelebration) && (
        <Box
          onClick={() => {
            setShowTitle(false);
            setShowCelebration(false);
          }}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 9999,
            cursor: "pointer",
          }}
        >
          <AnimatePresence>
            {showCelebration && (
              <Confetti
                width={window.innerWidth - 20}
                height={window.innerHeight}
                numberOfPieces={300}
              />
            )}
          </AnimatePresence>

          <AnimatePresence>
            {showTitle && (
              <motion.div
                key="title"
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ opacity: 1, scale: 1.2 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.5, ease: "easeInOut" }}
                style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  padding: "50px",
                  background:
                    "radial-gradient(circle at center, rgba(0, 255, 100, 0.05) 0%, rgba(0, 0, 0, 0.4) 100%)",
                  backdropFilter: "blur(2px)",
                  pointerEvents: "auto",
                  zIndex: 10000,
                  cursor: "pointer",
                }}
              >
                <motion.div style={{ textAlign: "center" }}>
                  <motion.img
                    key={images[imageIndex]}
                    src={images[imageIndex]}
                    alt="Logo Alien Realty"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.6 }}
                    style={{
                      width: imageIndex === 0 ? "300px" : "700px",
                      maxWidth: "80vw",
                      margin: "0 auto",
                      display: "block",
                      cursor: "default",
                    }}
                  />

                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { xs: "2rem", md: "2.5rem" },
                      fontWeight: "bold",
                      color: "#00FF40",
                      textShadow: "3px 3px 15px rgba(0, 0, 0, 0.5)",
                      letterSpacing: "3px",
                      textAlign: "center",
                      mt: 2,
                      cursor: "default",
                    }}
                  >
                    🎉¡{totalProperties} propiedades listadas!🛸
                  </Typography>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </Box>
      )}

      <main style={{ flex: 1 }}>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "80vh",
              backgroundColor: "#fafafa",
            }}
          >
            <PulseLoader color="#36d7b7" size={15} margin={2} />
            <Typography
              variant="h6"
              color="textSecondary"
              sx={{ mt: 3, textAlign: "center" }}
            >
              Cargando propiedades, por favor espera...
            </Typography>
          </Box>
        ) : error ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "80vh",
              textAlign: "center",
              gap: 2,
              backgroundColor: "white",
            }}
          >
            <NotInterestedIcon sx={{ fontSize: 75, color: "text.secondary" }} />
            <Typography variant="h6" color="error">
              Ocurrió un error
            </Typography>
            <Typography variant="body1" color="textSecondary">
              No pudimos cargar las propiedades. Por favor, inténtalo nuevamente
              más tarde.
            </Typography>
          </Box>
        ) : (
          <Box
            className="property-list"
            sx={{
              minHeight: "80vh",
              height: "auto",
              alignContent:
                filteredProperties.length > 0 ? "flex-start" : "center",
              p: viewMode === "list" ? 3 : 2,
              paddingTop: 7,
              paddingBottom: 2,
            }}
          >
            {viewMode === "list" ? (
              filteredProperties.length > 0 ? (
                <>
                  <Grid container spacing={3}>
                    {currentProperties.map((property) => {
                      return (
                        <Grid
                          size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 3 }}
                          key={property.id_Property}
                        >
                          <PropertyCard
                            property={property}
                            onConvertPrice={handleOpenModal}
                            hunterFullName={property.hunterFullName}
                            hunterProfileImage={property.hunterProfileImage}
                          />
                        </Grid>
                      );
                    })}
                  </Grid>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      marginTop: "auto",
                      pt:3
                    }}
                  >
                    <Pagination
                      count={Math.min(Math.ceil(totalProperties / itemsPerPage), maxAllowedPage)}
                      page={currentPage}
                      onChange={handlePageChange}
                      color="primary"
                    />
                  </Box>
                </>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: "50vh",
                    textAlign: "center",
                    color: "text.secondary",
                    gap: 2,
                  }}
                >
                  <NotInterestedIcon
                    sx={{ fontSize: 75, color: "text.secondary" }}
                  />
                  <Typography variant="h6">
                    No hay propiedades en este momento.
                  </Typography>
                  <Typography variant="body1" color="textSecondary">
                    Intenta ajustar los filtros o vuelve más tarde para ver
                    nuevas propiedades.
                  </Typography>
                </Box>
              )
            ) : (
              <MapView
                properties={filteredProperties}
                onOpenModal={handleOpenModal}
                selectedCurrency={selectedCurrency}
                buyRate={buyRate ?? 0}
                sellRate={sellRate ?? 0}
              />
            )}
            <PriceConversionModal
              open={isModalOpen}
              onClose={() => setModalOpen(false)}
              initialPrice={selectedPrice}
              currency={selectedCurrency2}
            />
          </Box>
        )}
      </main>
      <ChatBubble />
      <a
        target="_blank"
        href={"https://api.whatsapp.com/send?phone=15551418916"}
        rel="noreferrer"
      >
        <Fab
          color= "primary"
          aria-label="whatsapp"
          sx={{
            position: "fixed",
            bottom: { xs: 150, sm: 65 },
            right: 16,
            display: "flex",
            alignItems: "center",
            gap: 1,
            width: "140px",
            height: "40px",
            borderRadius: "24px",
          }}
        >
          <a
            href="https://wa.me/50684725308"
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: "none", color: "inherit" }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span>Whatsapp</span>
              <WhatsAppIcon />
            </div>
          </a>
        </Fab>
      </a>
      <Fab
        color="primary"
        aria-label="toggle view"
        sx={{
          position: "fixed",
          bottom: { xs: 100, sm: 16 }, // 100px en móvil, 16px en desktop
          right: 16,
          display: "flex",
          alignItems: "center",
          gap: 1,
          width: "140px",
          height: "40px",
          borderRadius: "24px",
        }}
        onClick={() => setViewMode(viewMode === "list" ? "map" : "list")}
      >
        {viewMode === "list" ? (
          <>
            <span>Vista mapa</span>
            <MapIcon />
          </>
        ) : (
          <>
            <span>Vista lista</span>
            <ListIcon />
          </>
        )}
      </Fab>
    </Box>
  );
};

export default HomePage;
